import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, UserPlus, UserCog } from "lucide-react";
import { fmtDate } from "@/lib/format";

const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };

const roleColor = (r) => ({
  super_admin: "border-gold/40 text-gold",
  org_admin: "border-red-500/40 text-red-400",
  producer: "border-purple-500/40 text-purple-400",
  production_staff: "border-blue-500/40 text-blue-400",
  support_staff: "border-cyan-500/40 text-cyan-400",
  cast: "border-emerald-500/40 text-emerald-400",
  parent: "border-orange-500/40 text-orange-400",
}[r] || "border-white/10 text-zinc-400");

const ROLE_LABEL = {
  super_admin: "Super Admin",
  org_admin: "Org Admin",
  producer: "Producer",
  production_staff: "Production Staff",
  support_staff: "Support Staff",
  cast: "Cast",
  parent: "Parent",
};

export default function PlatformUsers() {
  const { impersonateUser, rawUser } = useAuth();
  const nav = useNavigate();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "org_admin", phone: "" });
  const [busy, setBusy] = useState(false);
  const [impersonating, setImpersonating] = useState(null); // user id being impersonated (for spinner)

  const load = () => api.get("/platform/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return users.filter((u) => {
      if (roleFilter !== "all" && u.role !== roleFilter) return false;
      if (!term) return true;
      return (u.name || "").toLowerCase().includes(term) || (u.email || "").toLowerCase().includes(term);
    });
  }, [users, q, roleFilter]);

  const create = async () => {
    if (!form.email || !form.name || !form.password) return toast.error("Name, email, password required");
    if (form.password.length < 8) return toast.error("Password must be at least 8 characters");
    setBusy(true);
    try {
      await api.post("/platform/users", form);
      toast.success(`${form.role.replace("_", " ")} created`);
      setOpen(false);
      setForm({ email: "", name: "", password: "", role: "org_admin", phone: "" });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="platform-users">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-gold mb-1">Platform Admin</div>
          <h1 className="font-display text-4xl sm:text-5xl">All Users</h1>
          <p className="text-zinc-500 text-sm mt-2">Every user in the database. Create new client admin accounts here.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="platform-open-create-user-btn" style={goldBtn} className="h-10 px-4 rounded font-semibold text-sm inline-flex items-center gap-2">
              <UserPlus className="w-4 h-4" /> Create user
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#0A0A0A] border border-white/10 max-w-md">
            <DialogHeader><DialogTitle className="font-display text-2xl">Create user</DialogTitle></DialogHeader>
            <div className="space-y-3 pt-2">
              <div>
                <Label className="text-zinc-300 text-xs uppercase tracking-widest">Full name</Label>
                <Input data-testid="platform-create-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 bg-[#121212] border-white/10 h-11" />
              </div>
              <div>
                <Label className="text-zinc-300 text-xs uppercase tracking-widest">Email</Label>
                <Input data-testid="platform-create-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 bg-[#121212] border-white/10 h-11" />
              </div>
              <div>
                <Label className="text-zinc-300 text-xs uppercase tracking-widest">Temporary password</Label>
                <Input data-testid="platform-create-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-2 bg-[#121212] border-white/10 h-11" placeholder="Min 8 characters" />
                <div className="text-[11px] text-zinc-500 mt-1">Share this with the user; they can change it after signing in.</div>
              </div>
              <div>
                <Label className="text-zinc-300 text-xs uppercase tracking-widest">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="platform-create-role" className="mt-2 bg-[#121212] border-white/10 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10">
                    <SelectItem value="org_admin">Org Admin (client owner)</SelectItem>
                    <SelectItem value="producer">Producer (org-wide view + budget/announcements)</SelectItem>
                    <SelectItem value="production_staff">Production Staff (assigned to a show)</SelectItem>
                    <SelectItem value="support_staff">Support Staff (view-only)</SelectItem>
                    <SelectItem value="cast">Cast</SelectItem>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-300 text-xs uppercase tracking-widest">Phone (optional)</Label>
                <Input data-testid="platform-create-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2 bg-[#121212] border-white/10 h-11" />
              </div>
            </div>
            <DialogFooter>
              <button onClick={() => setOpen(false)} className="h-9 px-3 rounded border border-white/10 text-sm text-zinc-300 hover:bg-white/5">Cancel</button>
              <button data-testid="platform-create-user-btn" onClick={create} disabled={busy} style={goldBtn} className="h-9 px-4 rounded font-semibold text-sm">{busy ? "Creating…" : "Create user"}</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input data-testid="platform-users-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email" className="pl-9 bg-[#0F0F0F] border-white/10 h-10" />
        </div>
        <div className="flex items-center gap-1.5">
          {["all","super_admin","org_admin","producer","production_staff","support_staff","cast"].map((r) => (
            <button key={r} onClick={() => setRoleFilter(r)} data-testid={`role-filter-${r}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${roleFilter === r ? "bg-gold text-black border-gold font-semibold" : "border-white/15 text-zinc-300 hover:bg-white/5"}`}>
              {r === "all" ? "All" : (ROLE_LABEL[r] || r)}
            </button>
          ))}
        </div>
        <div className="text-xs text-zinc-500 ml-auto">{filtered.length} of {users.length}</div>
      </div>

      <div className="rounded-lg border border-white/10 overflow-hidden">
        {/* Horizontal scroll so the 6-col users table stays readable on
            phones instead of squishing every column into an unreadable
            strip. */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-[#121212] text-zinc-500 text-[10px] uppercase tracking-[0.2em]">
            <tr>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Email</th>
              <th className="text-left p-3">Role</th>
              <th className="text-left p-3">Productions</th>
              <th className="text-left p-3">Created</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => {
              const isSelf = rawUser?.id === u.id;
              const canImpersonate = !isSelf && u.role !== "super_admin";
              const startImpersonate = async () => {
                if (!canImpersonate) return;
                setImpersonating(u.id);
                try {
                  await impersonateUser(u.id);
                  toast.success(`You are now signed in as ${u.name || u.email}`);
                  // Land them on the productions picker so the target's
                  // membership list is what greets them.
                  nav("/productions");
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Could not impersonate");
                  setImpersonating(null);
                }
              };
              return (
                <tr key={u.id} className="border-t border-white/5" data-testid={`platform-user-row-${u.id}`}>
                  <td className="p-3 font-medium">{u.name}</td>
                  <td className="p-3 text-zinc-300">{u.email}</td>
                  <td className="p-3"><span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded border ${roleColor(u.role)}`}>{ROLE_LABEL[u.role] || u.role}</span></td>
                  <td className="p-3 text-zinc-400">{u.production_count}</td>
                  <td className="p-3 text-zinc-500 text-xs">{u.created_at ? fmtDate(u.created_at) : "—"}</td>
                  <td className="p-3 text-right">
                    <button
                      data-testid={`impersonate-btn-${u.id}`}
                      onClick={startImpersonate}
                      disabled={!canImpersonate || impersonating === u.id}
                      title={
                        isSelf ? "That's you"
                          : u.role === "super_admin" ? "Cannot impersonate another super admin"
                          : `See StageCall as ${u.name || u.email}`
                      }
                      className="text-[11px] inline-flex items-center gap-1.5 h-8 px-3 rounded border border-gold/40 text-gold hover:bg-gold/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    >
                      <UserCog className="w-3.5 h-3.5" />
                      {impersonating === u.id ? "Signing in…" : "Impersonate"}
                    </button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-zinc-500">No users match.</td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
